import {
  POST_COMPOSED_WEB_OPERATION,
  postComposedWeb,
  type PostComposedWebTransport,
} from 'src/api/checkout-widgets-client';
import {
  adaptWebResponse,
  createEmptyWebResponse,
  toWebWireRequest,
} from 'src/api/widget-data-contract';
import { type WebRequest } from 'src/types/widget-data';
import { fetchResponseWithRetry } from 'src/utils/fetch-with-retry';

const REQUEST_TIMEOUT = 2000;

const resolveCheckoutHost = () => {
  const checkoutHost = process.env.CHECKOUT_HOST;
  if (checkoutHost) {
    return checkoutHost;
  }

  if (typeof window === 'undefined') {
    return '';
  }

  const { hostname, origin } = window.location;
  if (hostname.endsWith('tabby.dev')) return 'https://checkout.tabby.dev';
  if (hostname.endsWith('tabby.ai')) return 'https://checkout.tabby.ai';

  return origin;
};

const getBaseUrl = () => `${resolveCheckoutHost()}/widgets/api`;

const retryingWebTransport: PostComposedWebTransport = (input, init) =>
  fetchResponseWithRetry({ input, init });

export const getWidgetData = async (body: WebRequest) => {
  const controller = new AbortController();
  const { signal } = controller;
  const timeout = setTimeout(() => {
    controller.abort();
  }, REQUEST_TIMEOUT);

  try {
    // The typed client binds this native request to the generated operation.
    // The injected transport preserves retries and browser-managed Referer.
    const response = await postComposedWeb(
      getBaseUrl(),
      toWebWireRequest(body),
      { signal, transport: retryingWebTransport }
    );

    if (response.status !== 200) {
      return createEmptyWebResponse();
    }

    return adaptWebResponse(await response.json());
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(
      `${POST_COMPOSED_WEB_OPERATION.method} ${POST_COMPOSED_WEB_OPERATION.path} failed`,
      error
    );

    return createEmptyWebResponse();
  } finally {
    clearTimeout(timeout);
  }
};
